import { useState } from "react";
import { User } from "@/common/model/User";
import { LBSubscriptionStatus } from "@/services/LBSubscriptionService";
import { ENDPOINTS } from "@/constants/api";

interface UserDetailsTabProps {
    user: User;
    subscriptionStatus: LBSubscriptionStatus;
    onUpdateEmail?: (newEmail: string) => Promise<void>;
}

export const UserDetailsTab: React.FC<UserDetailsTabProps> = ({ user, subscriptionStatus, onUpdateEmail }) => {
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [editedEmail, setEditedEmail] = useState(subscriptionStatus.username);
    const [isSaving, setIsSaving] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    const profileImageUrl = user.profilepicid
        ? `${ENDPOINTS.IMAGES.PROFILE}/${user.profilepicid}`
        : "/default-profilepic.png";

    const handleEditClick = () => {
        setIsEditingEmail(true);
        setEditedEmail(subscriptionStatus.username);
    };

    const handleCancelEdit = () => {
        setIsEditingEmail(false);
        setEditedEmail(subscriptionStatus.username);
    };

    const handleSaveEmail = async () => {
        if (!onUpdateEmail || editedEmail === subscriptionStatus.username) {
            setIsEditingEmail(false);
            return;
        }

        setIsSaving(true);
        try {
            await onUpdateEmail(editedEmail);
            setIsEditingEmail(false);
        } catch (error) {
            console.error("Error updating email:", error);
            setEditedEmail(subscriptionStatus.username);
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (timestamp: number) => new Date(timestamp).toLocaleDateString();
    const formatDateTime = (timestamp: number) => {
        const date = new Date(timestamp);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    return (
        <div className="space-y-4">
            {/* Profile Header - Compact */}
            <div className="flex items-start gap-4">
                <button
                    onClick={() => setShowImageModal(true)}
                    className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 rounded-full"
                >
                    <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="h-16 w-16 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    />
                </button>
                <div className="flex-1 min-w-0">
                    {/* Email with edit */}
                    <div className="flex items-center gap-2 mb-1">
                        {isEditingEmail ? (
                            <div className="flex items-center gap-1">
                                <input
                                    type="email"
                                    value={editedEmail}
                                    onChange={(e) => setEditedEmail(e.target.value)}
                                    className="text-sm text-gray-900 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-cyan-500 w-48"
                                    disabled={isSaving}
                                />
                                <button onClick={handleSaveEmail} disabled={isSaving} className="text-green-600 hover:text-green-700 disabled:opacity-50 p-0.5" title="Save">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                </button>
                                <button onClick={handleCancelEdit} disabled={isSaving} className="text-red-600 hover:text-red-700 disabled:opacity-50 p-0.5" title="Cancel">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        ) : (
                            <>
                                <span className="text-sm text-gray-900 truncate">{subscriptionStatus.username}</span>
                                {onUpdateEmail && (
                                    <button onClick={handleEditClick} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Edit email">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    {/* Quick stats row */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                        {subscriptionStatus.createdAt && (
                            <span className="flex items-center gap-1 text-sm text-gray-600">
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Joined {formatDateTime(subscriptionStatus.createdAt)}
                            </span>
                        )}
                        <span className="flex items-center gap-1 text-sm font-medium text-gray-700">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            {subscriptionStatus.kiteCount ?? 0} kites
                        </span>
                        <span className="flex items-center gap-1 text-sm font-medium text-gray-700">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            {subscriptionStatus.sessionsCount ?? 0} sessions
                            {(subscriptionStatus.hiddenSessionsCount ?? 0) > 0 && (
                                <span className="text-xs text-amber-600 ml-1">({subscriptionStatus.hiddenSessionsCount} hidden)</span>
                            )}
                        </span>
                        <span className="flex items-center gap-1 text-sm font-medium text-gray-700">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {subscriptionStatus.followersCount ?? 0} followers
                        </span>
                    </div>
                    {/* Social links inline */}
                    {(subscriptionStatus.socialInstagram || subscriptionStatus.socialTiktok) && (
                        <div className="flex items-center gap-3 mt-1">
                            {subscriptionStatus.socialInstagram && (
                                <a href={`https://instagram.com/${subscriptionStatus.socialInstagram}`} target="_blank" rel="noopener noreferrer" className="text-xs text-pink-600 hover:underline flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                                    @{subscriptionStatus.socialInstagram}
                                </a>
                            )}
                            {subscriptionStatus.socialTiktok && (
                                <a href={`https://tiktok.com/@${subscriptionStatus.socialTiktok}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-700 hover:underline flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" /></svg>
                                    @{subscriptionStatus.socialTiktok}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Two Column Layout for Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-3">
                    {/* Personal Info */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Personal Info
                        </h4>

                        {/* Name Cluster */}
                        <div className="mb-4 pb-4 border-b border-gray-200">
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Display Name</label>
                                    <span className="text-base text-gray-900 font-semibold">{subscriptionStatus.name || "-"}</span>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">First Name</label>
                                    <span className="text-base text-gray-900 font-medium">{subscriptionStatus.firstName || "-"}</span>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Last Name</label>
                                    <span className="text-base text-gray-900 font-medium">{subscriptionStatus.lastName || "-"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Other Personal Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Gender</label>
                                    <span className="text-sm text-gray-900 font-medium">{subscriptionStatus.gender || "-"}</span>
                                </div>
                            </div>
                            {subscriptionStatus.dateOfBirth && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Birthday</label>
                                        <span className="text-sm text-gray-900 font-medium">{formatDate(subscriptionStatus.dateOfBirth)}</span>
                                    </div>
                                </div>
                            )}
                            {subscriptionStatus.slug && (
                                <div className="flex items-center gap-3 col-span-2">
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Slug</label>
                                        <span className="text-sm text-gray-900 font-medium font-mono">{subscriptionStatus.slug}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Profile Details */}
                    {(subscriptionStatus.preferredSport || subscriptionStatus.skillLevel || subscriptionStatus.weightInKg || subscriptionStatus.homeSpotName) && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                                Profile
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                {subscriptionStatus.preferredSport && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Sport</label>
                                            <span className="text-sm text-gray-900 font-medium capitalize">{subscriptionStatus.preferredSport}</span>
                                        </div>
                                    </div>
                                )}
                                {subscriptionStatus.skillLevel && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Skill Level</label>
                                            <span className="text-sm text-gray-900 font-medium capitalize">{subscriptionStatus.skillLevel}</span>
                                        </div>
                                    </div>
                                )}
                                {subscriptionStatus.weightInKg && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Weight</label>
                                            <span className="text-sm text-gray-900 font-medium">{subscriptionStatus.weightInKg} kg</span>
                                        </div>
                                    </div>
                                )}
                                {subscriptionStatus.homeSpotName && (
                                    <div className="flex items-center gap-3 col-span-2">
                                        <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Home Spot</label>
                                            {subscriptionStatus.homeSpotId ? (
                                                <a
                                                    href={`https://discover.thesurfr.app/spots/${encodeURIComponent(subscriptionStatus.homeSpotName.toLowerCase().replace(/\s+/g, '-'))}/${subscriptionStatus.homeSpotId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-cyan-600 font-semibold hover:text-cyan-700 hover:underline flex items-center gap-1"
                                                >
                                                    {subscriptionStatus.homeSpotName}
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            ) : (
                                                <span className="text-sm text-gray-900 font-medium">{subscriptionStatus.homeSpotName}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Social Profile */}
                    {(subscriptionStatus.bio || subscriptionStatus.relationshipStatus || subscriptionStatus.lookingFor) && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                Social Profile
                            </h4>
                            {subscriptionStatus.bio && (
                                <div className="mb-3 pb-3 border-b border-gray-200">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Bio</label>
                                    <p className="text-sm text-gray-900 whitespace-pre-line">{subscriptionStatus.bio}</p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                {subscriptionStatus.relationshipStatus && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Status</label>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                subscriptionStatus.relationshipStatus === 'single' ? 'bg-green-100 text-green-800' :
                                                subscriptionStatus.relationshipStatus === 'taken' ? 'bg-blue-100 text-blue-800' :
                                                subscriptionStatus.relationshipStatus === 'complicated' ? 'bg-orange-100 text-orange-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {subscriptionStatus.relationshipStatus.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {subscriptionStatus.lookingFor && (
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block">Looking For</label>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                                                {subscriptionStatus.lookingFor.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* How They Found Us */}
                    {subscriptionStatus.hearAboutUsText && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Heard about Surfr from
                            </h4>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span className="text-base text-gray-900 font-semibold">{subscriptionStatus.hearAboutUsText}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                    {/* Personal Records */}
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-lg p-4">
                        <h4 className="text-xs font-bold text-cyan-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Personal Records
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded-lg px-3 py-2 text-center shadow-sm">
                                <div className="text-xl font-bold text-cyan-600">
                                    {subscriptionStatus.maxheight ? `${subscriptionStatus.maxheight.toFixed(1)}m` : "-"}
                                </div>
                                <div className="text-xs font-medium text-gray-600">Height</div>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 text-center shadow-sm">
                                <div className="text-xl font-bold text-cyan-600">
                                    {subscriptionStatus.maxairtime ? `${subscriptionStatus.maxairtime.toFixed(1)}s` : "-"}
                                </div>
                                <div className="text-xs font-medium text-gray-600">Airtime</div>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 text-center shadow-sm">
                                <div className="text-xl font-bold text-cyan-600">
                                    {subscriptionStatus.maxdistance ? `${subscriptionStatus.maxdistance.toFixed(0)}m` : "-"}
                                </div>
                                <div className="text-xs font-medium text-gray-600">Distance</div>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 text-center shadow-sm">
                                <div className="text-xl font-bold text-cyan-600">
                                    {subscriptionStatus.maxspeed ? `${(subscriptionStatus.maxspeed * 3.6).toFixed(0)}km/h` : "-"}
                                </div>
                                <div className="text-xs font-medium text-gray-600">Speed</div>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                            </svg>
                            Stats
                        </h4>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="bg-gray-100 rounded-lg p-3">
                                <div className="text-2xl font-bold text-gray-900">{subscriptionStatus.kiteCount ?? 0}</div>
                                <div className="text-xs font-bold text-gray-600 uppercase">Kites</div>
                                {subscriptionStatus.archivedKiteCount ? (
                                    <div className="text-xs text-gray-500 mt-0.5">({subscriptionStatus.archivedKiteCount} archived)</div>
                                ) : null}
                            </div>
                            <div className="bg-gray-100 rounded-lg p-3">
                                <div className="text-2xl font-bold text-gray-900">{subscriptionStatus.sessionsCount ?? 0}</div>
                                <div className="text-xs font-bold text-gray-600 uppercase">Sessions</div>
                                {(subscriptionStatus.hiddenSessionsCount ?? 0) > 0 && (
                                    <div className="text-xs text-amber-600 mt-0.5 flex items-center justify-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                        {subscriptionStatus.hiddenSessionsCount} hidden
                                    </div>
                                )}
                            </div>
                            <div className="bg-gray-100 rounded-lg p-3">
                                <div className="text-2xl font-bold text-gray-900">{subscriptionStatus.nReviews ?? 0}</div>
                                <div className="text-xs font-bold text-gray-600 uppercase">Reviews</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-center mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <div className="text-xl font-bold text-gray-900">{subscriptionStatus.followersCount ?? 0}</div>
                                    <div className="text-xs font-bold text-gray-600 uppercase">Followers</div>
                                </div>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <div className="text-xl font-bold text-gray-900">{subscriptionStatus.followingCount ?? 0}</div>
                                    <div className="text-xs font-bold text-gray-600 uppercase">Following</div>
                                </div>
                            </div>
                        </div>
                        {/* Stokes Balance */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <div className="text-xl font-bold text-amber-600">{subscriptionStatus.stokesBalance ?? 0}</div>
                                    <div className="text-xs font-bold text-gray-600 uppercase">Stokes Balance</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Image Modal */}
            {showImageModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
                    onClick={() => setShowImageModal(false)}
                >
                    <div className="relative max-w-2xl max-h-[90vh] p-2">
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute -top-2 -right-2 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors z-10"
                        >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <img
                            src={profileImageUrl}
                            alt="Profile"
                            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
